import { get, isEqual } from "lodash";
import React, { useContext, useLayoutEffect, useMemo, useState } from "react";
import { Subject } from "rxjs/internal/Subject";
import { Subscription } from "rxjs/internal/Subscription";
import { distinctUntilChanged, map, tap } from "rxjs/operators";
import { pickValue, validateField } from "./fieldHelpers";
import { FormContext } from "./FormContext";
import {
  FieldActionTypes,
  IFieldMeta,
  IFieldState,
  IFormState,
  TFieldValue,
  TValidator
} from "./formInterfaces";
import useDeepCompare from "./useDeepCompare";
import { isNil, isEmpty } from "./utils";

interface IField {
  name: string;
  defaultValue: TFieldValue;
  validate?: TValidator;
  checkWarning?: (value: TFieldValue) => string | undefined;
  format?: (value: TFieldValue) => TFieldValue;
  parse?: (value: TFieldValue) => TFieldValue;
  normalize?: (value: TFieldValue) => TFieldValue;
  destroyValueOnUnmount?: boolean;
  required?: boolean;
}

export const useField = (props: IField) => {
  const { dispatch, subscribe, fieldPrefix } = useContext(FormContext);
  const prefixedName = `${fieldPrefix || ""}${props.name}`;

  const [fieldValue, setFieldValue] = useState(props.defaultValue);
  const [fieldMeta, setFieldMeta] = useState<IFieldMeta>({});

  const { registerField, onChange, formatValue } = useMemo(() => {
    const parseValue = (value: TFieldValue): TFieldValue => {
      const { parse, normalize } = props;
      if (parse && typeof parse === "function") {
        value = parse(value);
      }

      if (normalize && typeof normalize === "function") {
        value = normalize(value);
      }

      return value;
    };

    const triggerChange = (
      evtOrValue: React.MouseEvent | TFieldValue,
      otherMeta?: IFieldMeta
    ) => {
      const { validate, required, checkWarning } = props;
      const value = parseValue(pickValue(evtOrValue));

      const requirdMessage =
        required && (isNil(value) || isEmpty(value))
          ? `${prefixedName} is required`
          : undefined;
      const error = requirdMessage || validateField(value, validate);

      const warning = checkWarning && checkWarning(value);

      const meta = {
        ...otherMeta,
        error,
        warning,
        completed: !error
      } as IFieldMeta;

      dispatch({
        name: prefixedName,
        type: FieldActionTypes.change,
        meta,
        payload: value
      });
    };

    return {
      registerField: ({ value, meta }: IFieldState) => {
        dispatch({
          name: prefixedName,
          type: FieldActionTypes.register,
          meta,
          payload: parseValue(value)
        });
      },
      onChange: (evtOrValue: React.MouseEvent | TFieldValue) =>
        triggerChange(evtOrValue),
      formatValue: (value: TFieldValue): TFieldValue => {
        const { format, normalize } = props;
        if (format && typeof format === "function") {
          value = format(value);
        }

        if (normalize && typeof normalize === "function") {
          value = normalize(value);
        }

        return value;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useDeepCompare(
    () => {
      registerField({
        value: props.defaultValue,
        meta: {
          required: !!props.required,
          completed: props.required
            ? !isNil(props.defaultValue) && !isEmpty(props.defaultValue)
            : true,
          defaultValue: props.defaultValue
        }
      });
    },
    { defaultValue: props.defaultValue, required: !!props.required }
  );

  useLayoutEffect(() => {
    let formStateSubscription: Subscription | null = null;

    const onFormStateChange = () => {
      const formStateObserver$ = new Subject<IFormState>();

      formStateObserver$
        .pipe(
          map(({ fields, values }) => ({
            meta: fields[prefixedName],
            value: get(values, prefixedName)
          })),
          distinctUntilChanged(
            (next, prev) =>
              isEqual(next.meta, prev.meta) && isEqual(prev.value, next.value)
          ),
          tap(({ meta, value }) => {
            if (meta || value) {
              setFieldValue(value);
              setFieldMeta(meta);
            }
          })
        )
        .subscribe();

      formStateSubscription = subscribe(formStateObserver$);
    };

    // should register observers before register field, otherwise the last field will lost field state
    // this cause the last field render even if I only changed the first field

    onFormStateChange();

    registerField({
      value: fieldValue,
      meta: {
        required: !!props.required,
        completed: props.required
          ? !isNil(fieldValue) && !isEmpty(fieldValue)
          : true,
        defaultValue: fieldValue
      }
    });

    return () => {
      dispatch({
        name: prefixedName,
        type: FieldActionTypes.destroy,
        meta: {
          destroyValueOnUnmount: props.destroyValueOnUnmount
        }
      });

      if (formStateSubscription) {
        formStateSubscription.unsubscribe();
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    value: formatValue(fieldValue),
    meta: fieldMeta,
    name: prefixedName,
    onChange
  };
};
